import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/css/Styles1.css';
import './CRUD.css';
import './CargosCRUD.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNotification } from '../components/NotificationSystem';
import SEO from '../components/SEO';
import Icon from '../components/Icon';

// Valores de la plantilla oficial de cargos (docs/Plantilla empleados y cargo.xlsx).
// En la base casi todos están vacíos porque esa columna nunca se cargó.
const PROCESOS_GESTION = [
  { value: 'ADMINISTRACION', label: 'Administración' },
  { value: 'PROYECTO', label: 'Proyecto' }
];

function etiquetaProceso(valor) {
  const encontrado = PROCESOS_GESTION.find(p => p.value === valor);
  return encontrado ? encontrado.label : (valor || 'N/A');
}

function CargosCRUD({ onLogout }) {
  const { success, error: showError } = useNotification();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCargo, setCurrentCargo] = useState({
    id_cargo: '',
    nombre_cargo: '',
    objetivo_cargo: '',
    proceso_gestion: '',
    id_banco: ''
  });

  const [cargos, setCargos] = useState([]);
  const [cargosFiltrados, setCargosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [niveles, setNiveles] = useState([]);
  const [nivelesError, setNivelesError] = useState('');

  const apiUrl = process.env.REACT_APP_API_BASE_URL || '';

  useEffect(() => {
    fetchCargos();
    fetchNiveles();
  }, []);

  // Niveles organizacionales / tipos de evaluación aplicables (banco_competencias).
  // Ver database/migrations/011_cargo_nivel_organizacional.sql.
  const fetchNiveles = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/cargos/niveles`);
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        setNiveles([]);
        setNivelesError(result?.message || 'No se pudo cargar el catálogo de niveles.');
        return;
      }
      setNiveles(result.data || []);
      setNivelesError('');
    } catch (error) {
      setNiveles([]);
      setNivelesError('No se pudo cargar el catálogo de niveles.');
    }
  };

  const fetchCargos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/cargos`);

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setCargos(result.data);
        setCargosFiltrados(result.data);
      } else {
        throw new Error(result.message || 'Error al cargar cargos');
      }
    } catch (error) {
      setError(error.message || 'Error al cargar cargos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = (busqueda || '').toString().toLowerCase().trim();
    if (!q) {
      setCargosFiltrados(cargos);
      return;
    }
    const filtrados = (cargos || []).filter(c => {
      const id = (c.id_cargo ?? '').toString().toLowerCase();
      const nombre = (c.nombre_cargo ?? '').toString().toLowerCase();
      const objetivo = (c.objetivo_cargo ?? '').toString().toLowerCase();
      const proceso = (c.proceso_gestion ?? '').toString().toLowerCase();
      const nivel = (c.nivel_nombre ?? '').toString().toLowerCase();
      return id.includes(q) || nombre.includes(q) || objetivo.includes(q) || proceso.includes(q) || nivel.includes(q);
    });
    setCargosFiltrados(filtrados);
  }, [busqueda, cargos]);

  const handleBackClick = () => {
    navigate('/admin');
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setCurrentCargo({
      id_cargo: '',
      nombre_cargo: '',
      objetivo_cargo: '',
      proceso_gestion: '',
      id_banco: ''
    });
    setShowForm(true);
  };

  const handleEditClick = (cargo) => {
    setIsEditing(true);
    setCurrentCargo({ ...cargo, id_banco: cargo.id_banco ?? '' });
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('¿Está seguro que desea eliminar este cargo?')) {
      try {
        const response = await fetch(`${apiUrl}/api/cargos/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.message || `Error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        if (result.success) {
          setCargos(cargos.filter(cargo => cargo.id_cargo !== id));
          success('Cargo eliminado', 'Cargo eliminado con éxito');
        } else {
          throw new Error(result.message || 'Error al eliminar cargo');
        }
      } catch (error) {
        showError('Error al eliminar', `Error al eliminar cargo: ${error.message}`);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCargo({ ...currentCargo, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const isCreateOperation = !isEditing;
      const url = isCreateOperation
        ? `${apiUrl}/api/cargos`
        : `${apiUrl}/api/cargos/${currentCargo.id_cargo}`;

      const method = isCreateOperation ? 'POST' : 'PUT';

      // El objetivo del cargo no se edita aquí. Al actualizar no se envía,
      // para no borrar el texto que ya esté guardado en la base.
      const payload = {
        nombre_cargo: currentCargo.nombre_cargo,
        proceso_gestion: currentCargo.proceso_gestion || '',
        id_banco: currentCargo.id_banco === '' || currentCargo.id_banco == null
          ? null
          : Number(currentCargo.id_banco)
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || `Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        if (isCreateOperation) {
          const getResponse = await fetch(`${apiUrl}/api/cargos/${result.id}`);
          if (getResponse.ok) {
            const getData = await getResponse.json();
            if (getData.success) {
              setCargos([getData.data, ...cargos]);
            }
          } else {
            fetchCargos();
          }
        } else {
          // Se refresca desde el servidor en vez de fusionar currentCargo en el
          // estado local, porque nivel_codigo/nivel_nombre son derivados (vienen
          // del JOIN con banco_competencias) y no viven en el formulario.
          fetchCargos();
        }

        setShowForm(false);
        success(
          isCreateOperation ? 'Cargo creado' : 'Cargo actualizado',
          isCreateOperation ? 'Cargo creado con éxito' : 'Cargo actualizado con éxito'
        );
      } else {
        throw new Error(result.message || `Error al ${isCreateOperation ? 'crear' : 'actualizar'} cargo`);
      }
    } catch (error) {
      showError('Error de operación', `Error: ${error.message}`);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="crud-container cargos-crud">
        <Header onLogout={onLogout} />
        <main className="crud-main">
          <div className="loading">Cargando cargos...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="crud-container cargos-crud">
        <Header onLogout={onLogout} />
        <main className="crud-main">
          <div className="error">Error: {error}</div>
          <button onClick={fetchCargos}>Reintentar</button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Gestión de Cargos - Panel de Administración"
        description="Gestiona los cargos y posiciones en el sistema de evaluación de desempeño de Meridian Consulting LTDA. Administra la estructura organizacional y roles del personal."
        keywords="gestión cargos, administración posiciones, estructura organizacional, roles personal, Meridian Consulting, evaluación desempeño"
        url="https://evaluacion.meridianltda.com/admin/cargos"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Gestión de Cargos - Panel de Administración",
          "description": "Página de gestión de cargos del sistema de evaluación de desempeño",
          "url": "https://evaluacion.meridianltda.com/admin/cargos",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Sistema de Evaluación de Desempeño - Meridian Consulting LTDA",
            "url": "https://evaluacion.meridianltda.com"
          },
          "about": {
            "@type": "Thing",
            "name": "Gestión de Cargos y Posiciones"
          }
        }}
      />
      <div className="crud-container cargos-crud">
        <Header onLogout={onLogout} />

        <main className="crud-main">
          <div className="crud-header">
            <button className="back-button" onClick={handleBackClick}>
              Volver al Panel
            </button>
            <div className="crud-header-titles">
              <h1>Gestión de Cargos</h1>
              <p className="crud-header-subtitle">Administra la estructura de cargos del sistema</p>
            </div>
            <button className="add-button" onClick={handleAddClick}>
              Agregar Cargo
            </button>
          </div>

          <div className="crud-search">
            <input
              type="text"
              placeholder="Buscar por nombre, proceso o nivel..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          {showForm && (
            <div className="crud-form-container">
              <form
                className="crud-form"
                onSubmit={handleSubmit}
              >
                <h2>{isEditing ? 'Editar Cargo' : 'Nuevo Cargo'}</h2>

                <div className="form-body">
                  <div className="form-group">
                    <label htmlFor="nombre_cargo">Nombre del Cargo:</label>
                    <input
                      type="text"
                      id="nombre_cargo"
                      name="nombre_cargo"
                      value={currentCargo.nombre_cargo}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="proceso_gestion">Proceso de Gestión:</label>
                    <select
                      id="proceso_gestion"
                      name="proceso_gestion"
                      value={currentCargo.proceso_gestion || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Sin asignar</option>
                      {PROCESOS_GESTION.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                      {currentCargo.proceso_gestion
                        && !PROCESOS_GESTION.some(p => p.value === currentCargo.proceso_gestion) && (
                        <option value={currentCargo.proceso_gestion}>
                          {currentCargo.proceso_gestion}
                        </option>
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="id_banco">Nivel Organizacional / Tipo de Evaluación:</label>
                    <select
                      id="id_banco"
                      name="id_banco"
                      value={currentCargo.id_banco == null ? '' : String(currentCargo.id_banco)}
                      onChange={handleInputChange}
                      disabled={Boolean(nivelesError)}
                    >
                      <option value="">Sin nivel asignado</option>
                      {niveles.map(n => (
                        <option key={n.id_banco} value={n.id_banco}>
                          {n.nombre} ({n.codigo})
                        </option>
                      ))}
                    </select>
                    <small className={nivelesError ? 'form-hint form-hint-error' : 'form-hint'}>
                      {nivelesError
                        ? nivelesError
                        : 'Determina el banco de competencias que aplicará a los empleados con este cargo.'}
                    </small>
                  </div>
                </div>

                <div className="form-buttons">
                  <button type="button" className="cancel-button" onClick={handleCancelForm}>
                    Cancelar
                  </button>
                  <button type="submit" className="save-button">
                    {isEditing ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="crud-table-container">
            {(cargosFiltrados || []).length === 0 ? (
              <p className="no-data">No hay cargos registrados</p>
            ) : (
              <table className="crud-table">
                <thead>
                  <tr>
                    <th>Nombre del Cargo</th>
                    <th>Proceso de Gestión</th>
                    <th>Nivel Organizacional / Tipo de Evaluación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cargosFiltrados.map(cargo => (
                    <tr key={cargo.id_cargo}>
                      <td>{cargo.nombre_cargo}</td>
                      <td>{etiquetaProceso(cargo.proceso_gestion)}</td>
                      <td>
                        {cargo.nivel_nombre ? (
                          <span className="nivel-badge">{cargo.nivel_nombre} ({cargo.nivel_codigo})</span>
                        ) : (
                          <span className="nivel-badge nivel-badge-sin-asignar">Sin nivel asignado</span>
                        )}
                      </td>
                      <td className="acciones">
                        <div className="acciones-inner">
                          <button
                            className="edit-button"
                            onClick={() => handleEditClick(cargo)}
                          >
                            <Icon name="edit" size={16} title="Editar" />
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteClick(cargo.id_cargo)}
                          >
                            <Icon name="trash" size={16} title="Eliminar" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default CargosCRUD;
